import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Tooltip as MuiTooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import CampaignIcon from '@mui/icons-material/Campaign';
import api from '../services/api';
import { Event } from '../types';
import { formatDate } from '../utils/format';
// import { formatCurrency } from '../utils/format';
import { useNotification } from '../context/NotificationContext';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

export const Statistics: React.FC = () => {
  const { showError, showSuccess } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | ''>('');
  const [statistics, setStatistics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sponsorReport, setSponsorReport] = useState<any>(null);
  const [sponsorLoading, setSponsorLoading] = useState(false);
  const [sponsorDialogOpen, setSponsorDialogOpen] = useState(false);
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await api.get('/api/reference/events');
        setEvents(response.data);
        if (response.data.length > 0 && !selectedEventId) {
          setSelectedEventId(response.data[0].id);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      fetchStatistics();
      
      // Set up auto-refresh every 30 seconds
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
      
      autoRefreshIntervalRef.current = setInterval(() => {
        if (selectedEventId && document.visibilityState === 'visible') {
          fetchStatistics(true); // Silent refresh
        }
      }, 30000); // 30 seconds

      return () => {
        if (autoRefreshIntervalRef.current) {
          clearInterval(autoRefreshIntervalRef.current);
        }
      };
    }
  }, [selectedEventId]);

  const fetchStatistics = async (silent = false) => {
    if (!silent) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    try {
      const response = await api.get('/api/statistics', {
        params: { eventId: selectedEventId },
      });
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      if (!silent) {
        showError('Ошибка загрузки статистики');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  };

  const handleManualRefresh = () => {
    fetchStatistics();
  };

  const handleExport = async (format: 'excel' | 'csv') => {
    try {
      const response = await api.get(`/api/statistics/export/${format}`, {
        params: { eventId: selectedEventId },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `statistics_${selectedEventId}.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      console.error('Error exporting:', error);
      showError(error.response?.data?.error || 'Ошибка экспорта');
    }
  };

  const fetchSponsorReport = async () => {
    if (!selectedEventId) return;
    setSponsorLoading(true);
    try {
      const response = await api.get(`/api/statistics/sponsor-report/${selectedEventId}`);
      setSponsorReport(response.data);
      setSponsorDialogOpen(true);
    } catch (error: any) {
      console.error('Error fetching sponsor report:', error);
      showError(error.response?.data?.error || 'Ошибка формирования отчёта для спонсоров');
    } finally {
      setSponsorLoading(false);
    }
  };

  const handleSponsorPdf = async () => {
    try {
      const response = await api.get(`/api/statistics/export/sponsor-pdf/${selectedEventId}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sponsor_report_${selectedEventId}_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('PDF отчёт для спонсоров скачан');
    } catch (error: any) {
      console.error('Error exporting sponsor PDF:', error);
      showError(error.response?.data?.error || 'Ошибка экспорта PDF');
    }
  };

  if (!selectedEventId) {
    return (
      <Box>
        <Typography>Выберите событие</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: 2 }}>
        <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
          <InputLabel>Событие</InputLabel>
          <Select
            value={selectedEventId}
            label="Событие"
            onChange={(e) => setSelectedEventId(e.target.value as number)}
          >
            {events.map((event) => (
              <MenuItem key={event.id} value={event.id}>
                {event.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' }, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
          <MuiTooltip title="Обновить статистику">
            <IconButton onClick={handleManualRefresh} disabled={refreshing || loading}>
              <RefreshIcon className={refreshing ? 'rotating' : ''} />
            </IconButton>
          </MuiTooltip>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => handleExport('excel')}
            fullWidth={isMobile}
          >
            Экспорт Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => handleExport('csv')}
            fullWidth={isMobile}
          >
            Экспорт CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={async () => {
              try {
                const response = await api.get('/api/statistics/export/pdf', {
                  params: { eventId: selectedEventId },
                  responseType: 'blob',
                });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `statistics_${selectedEventId}_${Date.now()}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                showSuccess('PDF файл успешно экспортирован');
              } catch (error: any) {
                console.error('Error exporting PDF:', error);
                showError(error.response?.data?.error || 'Ошибка экспорта в PDF');
              }
            }}
            disabled={!selectedEventId}
            fullWidth={isMobile}
          >
            Экспорт PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<CampaignIcon />}
            onClick={fetchSponsorReport}
            disabled={!selectedEventId || sponsorLoading}
            fullWidth={isMobile}
            sx={{ color: 'white' }}
          >
            {sponsorLoading ? 'Формируем...' : 'Отчёт для спонсоров'}
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : statistics ? (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Всего регистраций
                  </Typography>
                  <Typography variant="h4">{statistics.overview?.totalRegistrations || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Коллективов
                  </Typography>
                  <Typography variant="h4">{statistics.overview?.totalCollectives || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Участников
                  </Typography>
                  <Typography variant="h4">{statistics.overview?.totalParticipants || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Дипломов
                  </Typography>
                  <Typography variant="h4">{statistics.overview?.totalDiplomas || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  По номинациям
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statistics.byNomination || []}
                      dataKey="count"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label
                    >
                      {(statistics.byNomination || []).map((_: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  По статусам оплат
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Оплачено', value: statistics.payments?.paid || 0 },
                        { name: 'Выступление оплачено', value: statistics.payments?.performancePaid || 0 },
                        { name: 'Дипломы оплачены', value: statistics.payments?.diplomasPaid || 0 },
                        { name: 'Не оплачено', value: statistics.payments?.unpaid || 0 },
                      ]}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label
                    >
                      {[0, 1, 2, 3].map((index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  По дисциплинам
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={statistics.byDiscipline || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  По возрастам
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={statistics.byAge || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      ) : null}

      <Dialog
        open={sponsorDialogOpen}
        onClose={() => setSponsorDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <CampaignIcon color="primary" />
          Отчёт для спонсоров
        </DialogTitle>
        <DialogContent dividers>
          {sponsorReport ? (
            <>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {sponsorReport.event.name}
              </Typography>
              {sponsorReport.event.startDate && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {formatDate(sponsorReport.event.startDate)}
                  {sponsorReport.event.endDate && ` — ${formatDate(sponsorReport.event.endDate)}`}
                </Typography>
              )}

              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {[
                  { label: 'Регистраций', value: sponsorReport.summary.totalRegistrations },
                  { label: 'Коллективов', value: sponsorReport.summary.totalCollectives },
                  { label: 'Участников', value: sponsorReport.summary.totalParticipants },
                  { label: 'Уникальных участников', value: sponsorReport.summary.uniqueParticipants },
                  { label: 'Городов', value: sponsorReport.summary.citiesCount },
                  { label: 'Дисциплин', value: sponsorReport.summary.disciplinesCount },
                  { label: 'Номинаций', value: sponsorReport.summary.nominationsCount },
                  { label: 'Поступления', value: `${sponsorReport.summary.totalRevenue.toLocaleString('ru-RU')} ₽` },
                ].map((item) => (
                  <Grid item xs={6} sm={3} key={item.label}>
                    <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{item.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {sponsorReport.summary.cities?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Города участников:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {sponsorReport.summary.cities.map((city: string) => (
                      <Chip key={city} label={city} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}

              {sponsorReport.topCollectives?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Самые активные коллективы:</Typography>
                  {sponsorReport.topCollectives.map((tc: any, index: number) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography>{index + 1}. {tc.name}</Typography>
                      <Typography color="text.secondary">{tc.registrations} рег.</Typography>
                    </Box>
                  ))}
                </>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSponsorDialogOpen(false)}>Закрыть</Button>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={handleSponsorPdf}
            sx={{ color: 'white' }}
          >
            Скачать PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

